import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import GeneralDialog from "@/Layouts/components/Dialogs/GeneralDialog";
import { ITermConditions, IUserTerms } from "@/Settings/types/Common/model";

import { useMutation } from "@tanstack/react-query";
import { updatePdfAvailability, updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import clsx from "clsx";

import { ButtonsTabsProps } from "@/Layouts/types/ButtonTabsList/ButtonTabListItem";
import ListTabsButtons from "@/Layouts/components/ScrollableButtonTabList";
import { Editor } from "@tiptap/core";
import RichTextEditorComponent from "@/Layouts/components/RichTextEditor";
import ActionButtonDialog from "@/Layouts/components/Dialogs/ActionButtonsDialog";
import { useSelector } from "react-redux";
import { IUser } from "@/DataPages/types/timeAndPayRates/models";
import moment from "moment";

type TTermsAndCondDialogProps = {
	locationId: string;
	dialogOpen: boolean;
	position: string;
	isDepartmental: boolean;
	termsAndConditionProps: ITermConditions[];
	inEditionBy?: IUserTerms;
	handlerDialog: (dialogOpen: boolean) => void;
	handlerFocus: () => void;
	allowEditionByUserActions: boolean;
};

const TermsAndConditionDialog = (props: TTermsAndCondDialogProps) => {
	const {
		locationId,
		dialogOpen,
		position,
		isDepartmental,
		termsAndConditionProps,
		inEditionBy,
		handlerFocus,
		handlerDialog,
		allowEditionByUserActions,
	} = props;
	//state to manage list of terms and condition content by every tab
	const [termsAndConditions, setTermsAndConditions] = useState<ITermConditions[]>(termsAndConditionProps);
	//state to manage tab keys of the ones changes the content
	const [savedTabs, setSavedTabs] = useState<string[]>([]);
	//state to manage active tab
	const [activeTermsButton, setActiveTermsButton] = useState(
		termsAndConditionProps.find((termItem) => termItem.sectionTitle.toLocaleLowerCase() === "introduction")?._id
	);
	//state to show/hide modal to exit
	const [showExitDialog, setShowExitDialog] = useState(false);
	//state to show/hide modal to save
	const [showSaveDialog, setShowSaveDialog] = useState(false);
	//state to control that focus on editor happen only once, to avoid unnecessary mutations
	const [hasEditorFocused, setHasEditorFocused] = useState(false);
	//state to manage if component is read only or editable
	const [editionMode, setEditionMode] = useState({
		editable: allowEditionByUserActions,
		editorName: inEditionBy?.fullName ?? "",
	});

	const user = useSelector(
		(state: {
			user: {
				user: IUser;
			};
		}) => state.user.user
	);

	const { mutate, mutateAsync, isPending } = useMutation({
		mutationFn: async (input: { position: string; inEditionBy: string; termConditions?: ITermConditions[] }) => {
			const termnsAndConditionMutationResponse = await updateSystemSettings(
				locationId,
				{ payplanTermConditions: input },
				"TERMS_AND_CONDITIONS"
			);
			if (termnsAndConditionMutationResponse.payplanTermConditions) {
				const termsByCurrentPosition = termnsAndConditionMutationResponse.payplanTermConditions.find(
					(terms: { position: string; termsAndConditionProps: ITermConditions[] }) => terms.position === position
				).termConditions;
				setTermsAndConditions(termsByCurrentPosition);
			}
		},
		onError: (error) => errorLogger(error),
	});

	const { mutateAsync: mutatePayPlans, isPending: isPendingPayPlan } = useMutation({
		mutationFn: async (input: { locationId: string; position: string; pdfAvailability: boolean; isDepartmental: boolean }) => {
			await updatePdfAvailability(locationId, input.position, input.pdfAvailability, input.isDepartmental);
		},
		onError: (error) => errorLogger(error),
	});

	const handleButtonSelector = (keyValue: string) => {
		setActiveTermsButton(keyValue);
	};

	const saveTermsAndCondition = async () => {
		try {
			await mutateAsync({
				position,
				inEditionBy: "",
				termConditions: termsAndConditions,
			});
			await mutatePayPlans({
				locationId,
				position,
				isDepartmental,
				pdfAvailability: false,
			});
			if (!isPending && !isPendingPayPlan) {
				setShowSaveDialog(false);
				handlerDialog(false);
			}
		} catch (error) {
			errorLogger("error");
		}
	};

	const handlerEditorUpdate = (editor: Editor) => {
		const updatedContent = editor?.getHTML();
		setTermsAndConditions((previousTerms) =>
			previousTerms.map((terms) => {
				if (terms._id === activeTermsButton) {
					return {
						...terms,
						updatedAt: moment().utc().format(),
						updatedBy: inEditionBy?._id ?? "",
						content: updatedContent,
					};
				}
				return {
					...terms,
					updatedAt: moment().utc().format(),
					updatedBy: inEditionBy?._id ?? "",
				};
			})
		);
	};

	const cleanEditionRecord = () => {
		mutate({
			position,
			inEditionBy: "",
		});
	};

	useEffect(() => {
		const listChangesKey: string[] = termsAndConditions.reduce((acc: string[], value, currentIndex) => {
			if (value.content !== termsAndConditionProps[currentIndex].content) {
				acc.push(value._id);
			}
			return acc;
		}, []);

		setSavedTabs(listChangesKey);
	}, [termsAndConditions, setSavedTabs]);

	useEffect(() => {
		const isCurrentUser = inEditionBy?._id === user._id;
		const editorName = isCurrentUser ? "" : inEditionBy?.fullName;
		const isEditorEditable = (!inEditionBy?._id || isCurrentUser) && allowEditionByUserActions;

		if (!isCurrentUser) {
			setTermsAndConditions(termsAndConditionProps);
			setSavedTabs([]);
		}
		setEditionMode({
			editable: isEditorEditable,
			editorName: editorName ?? "",
		});
	}, [inEditionBy]);

	//generate button tab list settings
	const buttonsTabsSettings: ButtonsTabsProps[] = termsAndConditions.reduce((acc: ButtonsTabsProps[], value) => {
		acc.push({
			id: value._id,
			handleButtonSelector: handleButtonSelector,
			selected: activeTermsButton === value._id,
			title: value.sectionTitle,
		});
		return acc;
	}, []);

	//generate editor default value only when button change
	const activeTermsAndConditions = useMemo(() => {
		return termsAndConditions.find((terms) => terms._id === activeTermsButton)?.content;
	}, [activeTermsButton, editionMode]);

	return (
		<Stack>
			{showExitDialog && (
				<ActionButtonDialog
					open={showExitDialog}
					modalTitle="You are about to exit without saving your changes. Do you wish to continue?"
					buttonsSettings={[
						{
							title: "Exit",
							bgColor: "var(--primary-color)",
							boxShadowHover: "0 0 10px 2px var(--primary-color-opacity-50)",
							action: () => {
								cleanEditionRecord();
								handlerDialog(false);
							},
						},
						{
							title: "Cancel",
							bgColor: "var(--gray-blue-color)",
							boxShadowHover: "0 0 10px 2px var(--gray-blue-color-opacity-50)",
							action: () => {
								setShowExitDialog(false);
							},
						},
					]}
				/>
			)}
			{showSaveDialog && (
				<ActionButtonDialog
					open={showSaveDialog}
					modalTitle="All Pay Plan PDFs for this position will be regenerated. Please make sure all changes are final.
							Do you wish to continue?"
					buttonsSettings={[
						{
							title: "Confirm",
							bgColor: "var(--primary-color)",
							boxShadowHover: "0 0 10px 2px var(--primary-color-opacity-50)",
							loading: isPending || isPendingPayPlan,
							disabled: isPending || isPendingPayPlan,
							action: () => {
								saveTermsAndCondition();
							},
						},
						{
							title: "Cancel",
							bgColor: "var(--gray-blue-color)",
							boxShadowHover: "0 0 10px 2px var(--gray-blue-color-opacity-50)",
							action: () => {
								setShowSaveDialog(false);
							},
						},
					]}
				/>
			)}
			<GeneralDialog
				title={`${position} Terms and Conditions`}
				iconTitle
				className={"general-dialog"}
				disableClickOut={true}
				open={dialogOpen}
				sx={{
					zIndex: 1000,
				}}
				onClose={() => {
					if (savedTabs.length === 0) {
						if (editionMode.editable) {
							cleanEditionRecord();
						}
						handlerDialog(false);
					} else {
						setShowExitDialog(true);
					}
				}}
			>
				<Box
					sx={{
						width: "75vw",
						maxWidth: "1350px",
						padding: "0 20px",
					}}
				>
					<ListTabsButtons buttonSettings={buttonsTabsSettings} />
					<RichTextEditorComponent
						editableArg={editionMode.editable}
						initialvalue={activeTermsAndConditions}
						handlerEditorUpdate={handlerEditorUpdate}
						handlerEditorFocus={() => {
							if (!hasEditorFocused && editionMode) {
								handlerFocus();
								setHasEditorFocused(true);
							}
						}}
					/>
					<Box height={"5px"} mt={"8px"}>
						{editionMode.editorName && (
							<Typography
								sx={{
									fontFamily: "PTSansProNarrow",
									fontSize: 12,
									fontWeight: 400,
									color: "var(--text-color)",
								}}
							>{`Currently being edited by ${editionMode.editorName}`}</Typography>
						)}
					</Box>
					<Stack direction="row" justifyContent={"end"} mb="30px" mt="20px">
						<Button
							variant="contained"
							className={clsx({
								"!rounded-md !py-[5px] !text-sm !text-nowrap !text-white !capitalize !font-sans": true,
							})}
							sx={{
								maxWidth: "max-content",
								bgcolor: "var(--primary-color)",
								boxShadow: "none",
								"&:hover": {
									bgcolor: "var(--primary-color)",
									boxShadow: "0 0 10px 2px var(--primary-color-opacity-50)",
								},
								"&:disabled": {
									bgcolor: "var(--disabled-and-stroke-color)",
								},
							}}
							disabled={savedTabs.length === 0}
							disableElevation
							onClick={() => {
								setShowSaveDialog(true);
							}}
						>
							Save Changes
						</Button>
					</Stack>
				</Box>
			</GeneralDialog>
		</Stack>
	);
};

export default TermsAndConditionDialog;
